/**
 * useLocaleConfigs - Single Source of Truth for Locale Configuration
 * All locale fetching MUST come from this hook
 * Uses system_locale_configs table with correct column names
 */

import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';

export interface LocaleConfig {
  locale: string;
  displayName: string;
  englishDisplayName: string;
  countryCode: string | null;
  languageCode: string | null;
  isEnabled: boolean;
  isPrimary: boolean;
  primaryLocale: string | null; // Fallback target for regional locales
  isRtl: boolean; // Right-to-left language (from database is_rtl column)
}

export interface LocaleOption {
  code: string;
  name: string;
  englishName: string;
}

export function useLocaleConfigs(enabledOnly = true) {
  return useQuery({
    queryKey: ['locale-configs', enabledOnly],
    queryFn: async (): Promise<LocaleConfig[]> => {
      const filters = enabledOnly 
        ? [{ column: 'is_enabled', operator: 'eq' as const, value: true }]
        : [];

      const data = await db.fetchAll<{
        locale: string;
        display_name: string;
        english_display_name: string;
        country_code: string | null;
        language_code: string | null;
        is_enabled: boolean;
        is_primary: boolean;
        primary_locale: string | null;
        is_rtl: boolean;
      }>('system_locale_configs', 'locale, display_name, english_display_name, country_code, language_code, is_enabled, is_primary, primary_locale, is_rtl', {
        filters,
        orderBy: { column: 'english_display_name', ascending: true }
      });

      return (data || []).map(l => ({
        locale: l.locale,
        displayName: l.display_name,
        englishDisplayName: l.english_display_name,
        countryCode: l.country_code,
        languageCode: l.language_code,
        isEnabled: l.is_enabled,
        isPrimary: l.is_primary,
        primaryLocale: l.primary_locale,
        isRtl: l.is_rtl,
      }));
    },
    staleTime: 300000, // 5 minutes - locale configs rarely change
  });
}

/**
 * Helper to convert LocaleConfig to LocaleOption format
 * Used by components that expect the simpler format
 */
export function toLocaleOption(config: LocaleConfig): LocaleOption {
  return {
    code: config.locale,
    name: config.displayName,
    englishName: config.englishDisplayName,
  };
}

/**
 * Hook that returns locale configs in the simpler LocaleOption format
 */
export function useLocaleOptions(enabledOnly = true) {
  const { data, ...rest } = useLocaleConfigs(enabledOnly);
  
  return {
    data: data?.map(toLocaleOption),
    ...rest,
  };
}

/**
 * Hook that returns only primary locale configs
 * Used for metadata-related content (email templates, SEO, entity translations, etc.)
 */
export function usePrimaryLocaleConfigs() {
  const { data, ...rest } = useLocaleConfigs();
  
  return {
    data: data?.filter(locale => locale.isPrimary),
    ...rest,
  };
}

/**
 * Hook that returns only regional locale configs (non-primary)
 * Used for user preferences, display settings, language selectors with regional variants
 * 
 * Examples: en-US, es-MX, pt-BR (regional variants of en, es, pt primaries)
 */
export function useRegionalLocaleConfigs() {
  const { data, ...rest } = useLocaleConfigs();
  
  return {
    data: data?.filter(locale => !locale.isPrimary),
    ...rest,
  };
}
